import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Brain, Facebook, Globe, Linkedin, MapPin, PhoneCall, Search, X } from 'lucide-react';
import { crmApi, type ApiRecord } from '../../lib/api';

const STAGES: Array<{ value: string; label: string; tone: string }> = [
	{ value: 'new', label: 'New', tone: 'border-slate-400/30 bg-slate-500/10 text-secondary' },
	{ value: 'attempted', label: 'Attempted', tone: 'border-amber-400/30 bg-amber-500/10 text-amber-200' },
	{ value: 'contacted', label: 'Contacted', tone: 'border-sky-400/30 bg-sky-500/10 text-sky-200' },
	{ value: 'interested', label: 'Interested', tone: 'border-cyan-400/30 bg-accent/10 text-accent' },
	{ value: 'follow_up', label: 'Follow-up', tone: 'border-violet-400/30 bg-violet-500/10 text-violet-200' },
	{ value: 'future', label: 'Future', tone: 'border-indigo-400/30 bg-indigo-500/10 text-indigo-200' },
	{ value: 'not_interested', label: 'Not interested', tone: 'border-danger/30 bg-danger/10 text-danger' },
	{ value: 'converted', label: 'Converted', tone: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' }
];

function fullName(c: ApiRecord) {
	const n = `${String(c.first_name ?? '').trim()} ${String(c.last_name ?? '').trim()}`.trim();
	return n || String(c.email ?? `#${c.id}`);
}

function researchUrl(kind: 'maps' | 'linkedin' | 'facebook' | 'web' | 'google', c: ApiRecord) {
	const company = (c.company as ApiRecord | null)?.name as string | undefined;
	const domain = (c.company as ApiRecord | null)?.website_domain as string | undefined;
	const q = encodeURIComponent([fullName(c), company].filter(Boolean).join(' '));
	switch (kind) {
		case 'maps':
			return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(company ?? fullName(c))}`;
		case 'linkedin':
			return `https://www.linkedin.com/search/results/people/?keywords=${q}`;
		case 'facebook':
			return `https://www.facebook.com/search/top?q=${q}`;
		case 'web':
			return domain ? `https://${domain}` : `https://www.google.com/search?q=${q}`;
		case 'google':
		default:
			return `https://www.google.com/search?q=${q}`;
	}
}

export function LeadFocusPanel({ contactId, onClose }: { contactId: number; onClose: () => void }) {
	const queryClient = useQueryClient();
	const contactQuery = useQuery({
		queryKey: ['contact', contactId],
		queryFn: () => crmApi.contact(contactId)
	});
	const [followUpDate, setFollowUpDate] = useState('');
	const [followUpNote, setFollowUpNote] = useState('');
	const [enhanceResult, setEnhanceResult] = useState<string | null>(null);

	const updateStatus = useMutation({
		mutationFn: (status: string) => crmApi.updateContact(contactId, { lead_status: status }),
		onSuccess: () => {
			toast.success('Status updated');
			void queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
			void queryClient.invalidateQueries({ queryKey: ['leads'] });
		}
	});
	const scheduleFollowUp = useMutation({
		mutationFn: () =>
			crmApi.updateContact(contactId, {
				follow_up_date: followUpDate ? new Date(followUpDate).toISOString() : null,
				follow_up_note: followUpNote || null,
				lead_status: 'follow_up'
			}),
		onSuccess: () => {
			toast.success('Follow-up scheduled');
			setFollowUpDate('');
			setFollowUpNote('');
			void queryClient.invalidateQueries({ queryKey: ['contact', contactId] });
			void queryClient.invalidateQueries({ queryKey: ['leads'] });
		}
	});
	const enhance = useMutation({
		mutationFn: () => crmApi.leadEnhance(contactId),
		onSuccess: (data) => {
			setEnhanceResult(data.message);
			if (!data.configured) {
				toast.info('AI agent not configured');
			} else {
				toast.success('AI agent invoked');
			}
		},
		onError: (e) => toast.error(e instanceof Error ? e.message : 'Enhance failed')
	});

	const c = contactQuery.data;
	const currentStatus = String(c?.lead_status ?? 'new');

	return (
		<div className="fixed inset-0 z-50 flex items-stretch justify-end bg-canvas/70 backdrop-blur-sm">
			<aside className="flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l border-subtle bg-canvas p-5">
				<div className="flex items-start justify-between">
					<div>
						<p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-accent">Lead focus</p>
						<h2 className="mt-1 text-lg font-semibold text-primary">{c ? fullName(c) : 'Loading…'}</h2>
						<p className="text-xs text-muted">
							{c ? String((c.company as ApiRecord | null)?.name ?? 'No company') : ''}
							{c?.title ? ` · ${String(c.title)}` : ''}
						</p>
					</div>
					<button onClick={onClose} className="rounded-xl border border-subtle p-1.5 text-secondary hover:bg-surface-2">
						<X className="h-4 w-4" />
					</button>
				</div>

				{c ? (
					<>
						<section className="mt-4 rounded-xl border border-subtle bg-surface-2 p-3">
							<p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">Contact</p>
							<dl className="mt-1.5 grid grid-cols-2 gap-y-1 text-xs">
								<dt className="text-muted">Email</dt>
								<dd className="text-primary">{String(c.email ?? '—')}</dd>
								<dt className="text-muted">Phone</dt>
								<dd className="text-primary">{String(c.phone ?? '—')}</dd>
								<dt className="text-muted">Source</dt>
								<dd className="text-primary">{String(c.lead_source ?? '—')}</dd>
							</dl>
						</section>

						<section className="mt-3 rounded-xl border border-subtle bg-surface-2 p-3">
							<p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">Research</p>
							<div className="mt-2 flex flex-wrap gap-1.5">
								<ResearchLink href={researchUrl('google', c)} icon={Search} label="Google" />
								<ResearchLink href={researchUrl('maps', c)} icon={MapPin} label="Maps" />
								<ResearchLink href={researchUrl('linkedin', c)} icon={Linkedin} label="LinkedIn" />
								<ResearchLink href={researchUrl('facebook', c)} icon={Facebook} label="Facebook" />
								<ResearchLink href={researchUrl('web', c)} icon={Globe} label="Website" />
								<button
									onClick={() => enhance.mutate()}
									disabled={enhance.isPending}
									className="inline-flex items-center gap-1 rounded-lg border border-cyan-400/30 bg-accent/10 px-2 py-1 text-[11px] text-accent hover:bg-accent/15"
								>
									<Brain className="h-3 w-3" />
									{enhance.isPending ? 'Asking AI…' : 'Enhance with AI'}
								</button>
							</div>
							{enhanceResult ? <p className="mt-2 rounded-lg bg-surface-2/80 p-2 text-[11px] text-secondary">{enhanceResult}</p> : null}
						</section>

						<section className="mt-3 rounded-xl border border-subtle bg-surface-2 p-3">
							<p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">Stage</p>
							<div className="mt-2 flex flex-wrap gap-1.5">
								{STAGES.map((s) => (
									<button
										key={s.value}
										onClick={() => updateStatus.mutate(s.value)}
										disabled={updateStatus.isPending}
										className={`rounded-lg border px-2.5 py-1 text-[11px] transition ${
											currentStatus === s.value ? s.tone + ' shadow-panel' : 'border-subtle bg-canvas/40 text-muted hover:bg-surface-2'
										}`}
									>
										{s.label}
									</button>
								))}
							</div>
						</section>

						<section className="mt-3 rounded-xl border border-subtle bg-surface-2 p-3">
							<p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">Schedule follow-up</p>
							<div className="mt-2 grid gap-1.5 sm:grid-cols-2">
								<input
									type="datetime-local"
									value={followUpDate}
									onChange={(e) => setFollowUpDate(e.target.value)}
									className="rounded-lg border border-subtle bg-canvas px-2 py-1.5 text-xs text-primary"
								/>
								<input
									type="text"
									placeholder="Note (what to talk about)"
									value={followUpNote}
									onChange={(e) => setFollowUpNote(e.target.value)}
									className="rounded-lg border border-subtle bg-canvas px-2 py-1.5 text-xs text-primary"
								/>
							</div>
							<button
								onClick={() => scheduleFollowUp.mutate()}
								disabled={!followUpDate || scheduleFollowUp.isPending}
								className="mt-2 rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-accent-contrast disabled:opacity-40"
							>
								Schedule
							</button>
							{c.follow_up_date ? (
								<p className="mt-1.5 text-[11px] text-muted">
									Current: {new Date(String(c.follow_up_date)).toLocaleString()}
									{c.follow_up_note ? ` — ${String(c.follow_up_note)}` : ''}
								</p>
							) : null}
						</section>

						<section className="mt-3 rounded-xl border border-subtle bg-surface-2 p-3">
							<p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">Quick actions</p>
							<div className="mt-2 flex flex-wrap gap-1.5">
								{c.phone ? (
									<a
										href={`tel:${String(c.phone)}`}
										className="inline-flex items-center gap-1 rounded-lg border border-subtle bg-surface-2 px-2 py-1 text-[11px] text-secondary hover:bg-surface-hover"
									>
										<PhoneCall className="h-3 w-3" /> Call {String(c.phone)}
									</a>
								) : null}
								{c.email ? (
									<a
										href={`mailto:${String(c.email)}`}
										className="inline-flex items-center gap-1 rounded-lg border border-subtle bg-surface-2 px-2 py-1 text-[11px] text-secondary hover:bg-surface-hover"
									>
										Email {String(c.email)}
									</a>
								) : null}
							</div>
						</section>
					</>
				) : null}
			</aside>
		</div>
	);
}

function ResearchLink({ href, icon: Icon, label }: { href: string; icon: typeof Search; label: string }) {
	return (
		<a
			href={href}
			target="_blank"
			rel="noreferrer noopener"
			className="inline-flex items-center gap-1 rounded-lg border border-subtle bg-surface-2 px-2 py-1 text-[11px] text-secondary transition hover:bg-surface-hover"
		>
			<Icon className="h-3 w-3" />
			{label}
		</a>
	);
}
